/**
 * 逻辑流编辑器 - 自定义节点组件
 */

import React from 'react';
import {Handle, type NodeProps, Position} from '@xyflow/react';
import {Tag} from 'antd';

// ========== 通用样式 ==========
const nodeBaseStyle: React.CSSProperties = {
    padding: '10px 14px',
    borderRadius: '12px',
    border: '1px solid var(--pt-border)',
    background: 'rgba(12,17,32,0.95)',
    color: 'var(--pt-text-primary)',
    fontSize: '13px',
    minWidth: '180px',
    maxWidth: '260px',
    boxShadow: '0 4px 16px rgba(0,0,0,0.3)',
    backdropFilter: 'blur(12px)',
};

const headerStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '6px',
    fontWeight: 600,
    fontSize: '14px',
};

const dotStyle = (color: string): React.CSSProperties => ({
    width: '10px',
    height: '10px',
    borderRadius: '50%',
    background: color,
    flexShrink: 0,
});

const bodyStyle: React.CSSProperties = {
    fontSize: '12px',
    color: 'var(--pt-text-secondary)',
    lineHeight: 1.5,
    wordBreak: 'break-all',
};

// ========== INDICATOR 节点 ==========
export const IndicatorNode: React.FC<NodeProps> = ({data, selected}) => {
    const config = data.config as any;
    const outputType = config?.outputType || 'column';
    const borderColor = selected ? 'rgba(139,92,246,0.6)' : 'rgba(139,92,246,0.25)';

    const displayCode = 'Dict' === outputType
        ? `${config?.indicatorName || '?'} = ${config?.function || '?'}(...)`
        : `df['${config?.indicatorName || '?'}'] = ${config?.function || '?'}(...)`;

    return (
        <div style={{...nodeBaseStyle, borderColor}}>
            <div style={headerStyle}>
                <span style={dotStyle('#8B5CF6')}/>
                <span>{data.label as string}</span>
                {'Dict' === outputType && (
                    <Tag color="purple" style={{fontSize: '10px', marginLeft: 'auto'}}>Dict</Tag>
                )}
            </div>
            <div style={bodyStyle}>
                <code>{displayCode}</code>
            </div>
            <Handle type="target" position={Position.Left} style={{background: '#8B5CF6'}}/>
            <Handle type="source" position={Position.Right} style={{background: '#8B5CF6'}}/>
        </div>
    );
};

// ========== RULE 节点 ==========
export const RuleNode: React.FC<NodeProps> = ({data, selected}) => {
    const config = data.config as any;
    const rules = config?.rules || [];
    const logicOp = config?.logicOperator || 'AND';
    const action = config?.action || 'enter_long';

    const actionColorMap: Record<string, string> = {
        enter_long: 'var(--pt-bull)',
        enter_short: 'var(--pt-bear)',
        exit_long: '#F0B429',
        exit_short: '#8B5CF6',
        none: 'var(--pt-text-secondary)',
    };
    const actionLabelMap: Record<string, string> = {
        enter_long: '做多',
        enter_short: '做空',
        exit_long: '平多',
        exit_short: '平空',
        none: '组合',
    };

    const color = actionColorMap[action] || 'var(--pt-text-primary)';
    const borderColor = selected ? `${color}99` : `${color}40`;

    return (
        <div style={{...nodeBaseStyle, borderColor}}>
            <div style={headerStyle}>
                <span style={dotStyle(color)}/>
                <span>{data.label as string}</span>
            </div>
            <div style={{
                ...bodyStyle,
                background: 'rgba(240,180,41,0.06)',
                borderRadius: '6px',
                padding: '4px 8px',
                marginBottom: '4px'
            }}>
                {rules.length > 0 ? (
                    <div>
                        <span style={{color: '#F0B429', fontWeight: 500, fontSize: '10px'}}>{logicOp}</span>
                        {rules.map((r: any, i: number) => (
                            <div key={i} style={{marginTop: '1px'}}>
                                {r.leftOperand?.replace("dataframe['", '').replace("']", '') || '?'}
                                {' '}{r.operator}{' '}{r.rightOperand || '?'}
                            </div>
                        ))}
                    </div>
                ) : (
                    <span style={{color: 'var(--pt-text-secondary)'}}>未配置条件</span>
                )}
            </div>
            {'none' === action ? (
                <div style={{
                    color,
                    fontWeight: 500,
                    fontSize: '12px',
                    textAlign: 'center',
                    padding: '4px 0',
                    opacity: 0.7
                }}>
                    条件组合
                </div>
            ) : (
                <div style={{color, fontWeight: 600, fontSize: '15px', textAlign: 'center', padding: '4px 0'}}>
                    {actionLabelMap[action] || action}
                </div>
            )}
            <Handle type="target" position={Position.Left} style={{background: color}}/>
            <Handle type="source" position={Position.Right} style={{background: color}}/>
        </div>
    );
};

// ========== 节点类型映射（供 React Flow 使用） ==========
export const logicFlowNodeTypes = {
    indicatorNode: IndicatorNode,
    ruleNode: RuleNode,
};

/** 将逻辑节点类型映射为 React Flow 节点类型字符串 */
export function toRFNodeType(type: string): string {
    switch (type) {
        case 'INDICATOR':
            return 'indicatorNode';
        case 'RULE':
            return 'ruleNode';
        default:
            return 'indicatorNode';
    }
}
